import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { get } from "lodash";
import { Link } from "react-router-dom";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import ReactToPrint from "react-to-print";

import "./ExerciseListing.scss";
import {
	togglePreloader,
	getExerciseDetails,
	getRelatedExercises,
	addExerciseToMyWorkout,
	resetField,
	getMyProgressList
} from "../../../store/actions";
import ImageSlider from "../../../sharedComponents/imageSlider";
import PlaceholderIMG from "../../../sharedComponents/placeholderImage";
import VideoModal from "./exerciseVideoModal";
import PotentialBurn from "./potentialBurn";
import Tooltip from "../../../sharedComponents/tooltip";
import AddLogPanel from "../myProgress/addLogPanel";
import AddJouralExerciseForm from "../journal/add/addJournalExerciseForm";

const ExerciseDetails = ({
	match,
	history,
	getExerciseDetails,
	exerciseDetails,
	getRelatedExercises,
	relatedExercises,
	addExerciseToMyWorkout,
	resetField,
	getMyProgressList
}) => {
	const exerciseId = (match && match.params && match.params.id) || null;

	useEffect(() => {
		if (exerciseId) {
			getExerciseDetails(exerciseId);
			getRelatedExercises(exerciseId);
		}
	}, [exerciseId, getExerciseDetails, getRelatedExercises]);

	useEffect(() => {
		return () => {
			resetField("Exercises.exerciseDetails", {});
		};
	}, [resetField]);

	const { name, description, instruction_html, image_list, video } = exerciseDetails;
	const [journalExercise, setJournalExercise] = useState(null);

	const handleJournalExercise = () => {
		const newExercise = {
			exercise: exerciseDetails ? exerciseDetails.id : null,
			type_of_activity: exerciseDetails ? exerciseDetails.type : null,
			exercise_name: exerciseDetails ? exerciseDetails.name : null,
		};
		setJournalExercise(newExercise);
	};

	const sliderImages = image_list && image_list.length ? image_list.map((item) => ({ src: item.image.data })) : [];

	const videoImages =
		video && video.length ? video.map((item) => ({ thumbnail: `https://img.youtube.com/vi/${item}/0.jpg`, id: item })) : [];

	const componentRef = useRef(null);

	const renderRelatedExercises = () => {
		return relatedExercises
			? relatedExercises.map((item, index) => {
					return (
						<div key={index} className="mb-3">
							{item.data && item.data.length ? <h4 className="text-primary-dark mb-2">{item.label}</h4> : null}
							<ul className="related-items-list">
								{item.data.map((innerItem) => {
									return (
										<li key={innerItem.id} className="related-items-item">
											<Link to={`/fitness/exercises/${innerItem.id}/details`}>
												<span className="related-items-item-image">
													<PlaceholderIMG
														src={innerItem.src}
														width="80"
														height="60"
														alt={name}
														bg="rgba(0,0,0,0.1)"
														color="rgba(0,0,0,0.3)"
														objectFit="cover"
													/>
												</span>
												<span className="related-items-item-name text-truncate">{innerItem.name}</span>
											</Link>
										</li>
									);
								})}
							</ul>
						</div>
					);
			  })
			: null;
	};

	const [showVideoModal, toggleVideoModal] = useState(false);
	const [videoId, setVideoId] = useState(false);

	const setVideo = ({ id }) => {
		toggleVideoModal(true);
		setVideoId(id);
	};

	const [alreadyInPlan, setAlreadyInPlan] = useState(false);
	useEffect(() => {
		setAlreadyInPlan(exerciseDetails ? exerciseDetails.already_in_plan : false);
	}, [exerciseDetails]);

	const addToMyWorkout = () => {
		const successHandler = () => {
			NotificationManager.success("Successfully added!", "", 5000);
			setAlreadyInPlan(true);
			history.push("/fitness/my-workout");
		};
		addExerciseToMyWorkout({ is_plan: 0, exercise: exerciseId }, successHandler);
	};

	const renderNav = () => {
		return (
			<div className="block-overlay-header-nav d-flex">
				<Tooltip content="Add to My Plan">
					<button disabled={alreadyInPlan} className="btn btn-cell btn-light ml-2" onClick={addToMyWorkout}>
						<i className="icon icon-add-to-plan" />
					</button>
				</Tooltip>
				<ReactToPrint
					trigger={() => (
						<button className="btn btn-cell btn-light ml-2 print-hidden">
							<Tooltip content="Print">
								<i className="icon icon-print" />
							</Tooltip>
						</button>
					)}
					content={() => componentRef.current}
				/>
			</div>
		);
	};

	return exerciseDetails ? (
		<section className="page page-exercise-details">
			<div className="container">
				<div className="row" ref={componentRef}>
					<div className="col-9">
						<div className="block block-primary">
							<div className="relative">
								<ImageSlider list={sliderImages} />
								{renderNav()}
								<div className="block-overlay-header p-3">
									<h2 className="text-white">{name ? name : ""}</h2>
									{description && (
										<React.Fragment>
											<h4 className="text-white">Description</h4>
											<div dangerouslySetInnerHTML={{ __html: description }} />
										</React.Fragment>
									)}
								</div>
							</div>
						</div>
						<div className="block block-primary p-4">
							{videoImages && videoImages.length ? (
								<React.Fragment>
									<h4 className="text-primary-dark mt-2">Videos</h4>
									<div className="d-flex">
										{videoImages.map((item) => {
											return (
												<div key={item.id} className="pr-4 pb-4 pointer" onClick={() => setVideo(item)}>
													<PlaceholderIMG src={item.thumbnail} width="120" objectFit="cover" />
												</div>
											);
										})}
									</div>
								</React.Fragment>
							) : null}
							{instruction_html && (
								<React.Fragment>
									<h4 className="text-primary-dark mt-2">Instructions</h4>
									<div dangerouslySetInnerHTML={{ __html: instruction_html }} />
								</React.Fragment>
							)}
						</div>
					</div>
					<div className="col-3">
						<button
							type="button"
							className="btn btn-primary-dark w-100 mb-4 print-hidden"
							onClick={() => handleJournalExercise()}>
							Journal This
						</button>
						<AddJouralExerciseForm initialValues={journalExercise} onUpdate={() => setJournalExercise(null)} />
						<PotentialBurn exercise={exerciseDetails} />
						<div className="block block-primary p-4 print-hidden">{renderRelatedExercises()}</div>
					</div>
				</div>
			</div>
			<VideoModal show={showVideoModal} onHide={toggleVideoModal} videoId={videoId} />
			<AddLogPanel
				history={history}
				initialValues={{
					datedone: moment(exerciseDetails ? exerciseDetails.logDate : "").format("YYYY-MM-DD")
				}}
				progressList={getMyProgressList}
			/>
		</section>
	) : null;
};

const mapStateToProps = (state) => {
	const { Exercises, UI } = state;
	return {
		exerciseDetails: get(Exercises, "exerciseDetails", {}),
		relatedExercises: get(Exercises, "relatedExercises", []),
		atBottom: UI.atBottom
	};
};

const mapDispatchToProps = {
	togglePreloader,
	getExerciseDetails,
	getRelatedExercises,
	addExerciseToMyWorkout,
	resetField,
	getMyProgressList
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(ExerciseDetails);
